// ─────────────────────────────────────────────────────────────
// Integrations tab (iter265, restyled iter267, regrouped iter297)
//
// Layout only — no functional change. The four Foundation panels
// (Approval Queue · Bridge Ops · Settings · Mapping) now sit behind
// one "Foundation" sub-tab with a nested strip, Miter follows, and
// Push Notifications moved to Settings → General (last tab).
//
// Access: each sub-tab declares its access-matrix surface, so who
// sees what is managed in Settings → Users → Permissions.
// ─────────────────────────────────────────────────────────────
import { useState } from "react";
import { useUrlTab } from "@/lib/useUrlTab";
import { Cloud, Landmark, Inbox, Activity, Database, Eraser, ShieldAlert, Settings2, GitMerge, Send } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import MiterMappingPanel from "@/components/miter/MiterMappingPanel";
import FoundationApprovalQueuePanel from "@/components/integrations/FoundationApprovalQueuePanel";
import FoundationSyncQueue from "@/components/integrations/FoundationSyncQueue";
import FoundationSlaBadge from "@/components/integrations/FoundationSlaBadge";
import FoundationBridgeOpsPanel from "@/components/integrations/FoundationBridgeOpsPanel";
import FoundationSettingsPanel from "@/components/integrations/FoundationSettingsPanel";
import FoundationActualsCard from "@/components/integrations/FoundationActualsCard";
import FoundationMappingPanel from "@/components/integrations/FoundationMappingPanel";
import DataManagementPanel from "@/components/settings/DataManagementPanel";
import DataCleanupPanel from "@/components/settings/DataCleanupPanel";
import { SettingsPanel, SubTabs, EmptyState } from "@/components/settings/SettingsUI";

// Deep-link keys stay exactly as they were — the SLA badge and Cmd+K
// still pass e.g. {expand: "foundationApproval"} and land on the right
// nested tab.
export const INTEGRATION_SECTIONS = {
  foundationApproval: "foundationApproval",
  foundationSync: "foundationSync",
  foundationBridgeOps: "foundationBridgeOps",
  foundationSettings: "foundationSettings",
  foundationMapping: "foundationMapping",
  dataManagement: "dataManagement",
  dataCleanup: "dataCleanup",
};

const FOUNDATION_TABS = [
  { id: "foundationApproval", label: "Approval Queue", icon: Inbox, surface: "integrations.foundation_queue" },
  { id: "foundationSync", label: "Sync", icon: Send, surface: "integrations.foundation_queue" },
  { id: "foundationBridgeOps", label: "Bridge Ops", icon: Activity, surface: "integrations.bridge_ops" },
  { id: "foundationSettings", label: "Settings", icon: Settings2, surface: "integrations.foundation_queue" },
  { id: "foundationMapping", label: "Mapping", icon: GitMerge, surface: "integrations.foundation_queue" },
];
const FOUNDATION_IDS = FOUNDATION_TABS.map(t => t.id);

const ALL_SUB_TABS = [
  { id: "foundation", label: "Foundation", icon: Landmark, surface: "integrations.foundation_queue", keywords: ["Approval queue", "Sync queue", "Bridge", "Mapping", "Foundation settings", "Job cost"] },
  { id: "miter", label: "Miter", icon: Cloud, surface: "integrations.miter", keywords: ["Timekeeping", "Payroll"] },
  { id: "dataManagement", label: "Data Management", icon: Database, surface: "integrations.data_management", keywords: ["Import", "Export", "Backup"] },
  { id: "dataCleanup", label: "Go-Live Data Cleanup", icon: Eraser, surface: "integrations.data_cleanup", keywords: ["Reset", "Delete test data"] },
];

export default function IntegrationsTab({ initialSection, slaCount = 0, slaTitle = "" }) {
  const { can } = useAuth();
  const deepFoundation = FOUNDATION_IDS.includes(initialSection);

  const SUB_TABS = ALL_SUB_TABS
    .filter(t => (t.id === "foundation"
      ? FOUNDATION_TABS.some(f => can(f.surface, "read"))
      : can(t.surface, "read")))
    .map(t => (t.id === "foundation" && slaCount > 0
      ? { ...t, badge: <FoundationSlaBadge count={slaCount} title={slaTitle} testId="integrations-subtab-sla-badge" /> }
      : t));

  const [subTab, setSubTab] = useUrlTab("sub", deepFoundation ? "foundation" : (initialSection || "foundation"));
  const active = SUB_TABS.some(t => t.id === subTab) ? subTab : SUB_TABS[0]?.id;

  const FOUNDATION_VISIBLE = FOUNDATION_TABS
    .filter(t => can(t.surface, "read"))
    .map(t => (t.id === "foundationApproval" && slaCount > 0
      ? { ...t, badge: <FoundationSlaBadge count={slaCount} title={slaTitle} testId="foundation-subtab-sla-badge" /> }
      : t));
  const [fnTab, setFnTab] = useState(deepFoundation ? initialSection : "foundationApproval");
  const fnActive = FOUNDATION_VISIBLE.some(t => t.id === fnTab) ? fnTab : FOUNDATION_VISIBLE[0]?.id;

  if (SUB_TABS.length === 0) {
    return (
      <SettingsPanel testId="integrations-not-authorized">
        <EmptyState
          icon={ShieldAlert}
          title="Not authorized"
          hint="Integrations is restricted to full admins. Ask an admin to grant access in Users → Permissions."
        />
      </SettingsPanel>
    );
  }

  return (
    <div className="space-y-3" data-testid="integrations-tab">
      <SubTabs
        items={SUB_TABS}
        value={active}
        onChange={setSubTab}
        testId="integrations-subtabs"
        testIdPrefix="integrations-subtab"
        searchable
      />

      {active === "foundation" && (
        <div className="space-y-3" data-testid="foundation-group">
          <SubTabs
            items={FOUNDATION_VISIBLE}
            value={fnActive}
            onChange={setFnTab}
            testId="foundation-subtabs"
            testIdPrefix="foundation-subtab"
          />
          <SettingsPanel>
            {fnActive === "foundationApproval" && <FoundationApprovalQueuePanel />}
            {fnActive === "foundationSync" && <FoundationSyncQueue />}
            {fnActive === "foundationBridgeOps" && <FoundationBridgeOpsPanel isAdmin={true} />}
            {fnActive === "foundationSettings" && (<><div className="px-5 pt-4"><FoundationActualsCard /></div><FoundationSettingsPanel /></>)}
            {fnActive === "foundationMapping" && <FoundationMappingPanel />}
          </SettingsPanel>
        </div>
      )}

      {active === "miter" && <MiterMappingPanel />}

      {(active === "dataManagement" || active === "dataCleanup") && (
        <SettingsPanel>
          {active === "dataManagement" && <DataManagementPanel />}
          {active === "dataCleanup" && <DataCleanupPanel />}
        </SettingsPanel>
      )}
    </div>
  );
}
